// Y-axis bounds for the trend charts.
//
// Two problems it exists to solve, both visible before it did:
//
//   - Padding the data minimum by a fraction of the range put the axis below
//     zero on any series with a wide spread. Total DVM hours over a week runs
//     from a quiet Sunday to a full Monday, so 15% of that range subtracted
//     from the Sunday figure read "-5088h" -- an impossible quantity of hours,
//     and a fifth of the plot area spent drawing it.
//   - Passing a raw padded bound to Chart.js makes that exact number a tick, so
//     the axis was labelled "51847h" next to "40000h" and "0h".
//
// Bounds are therefore snapped outward onto a round step, and a series that is
// entirely non-negative never gets a negative axis. The step comes from the
// DATA range rather than the padded range: padding first and snapping second
// compounds the two, and a 15% pad that crosses a step boundary costs a whole
// extra step of empty plot.

function niceStep(range: number): number {
  if (range <= 0) return 1;
  const raw = range / 5;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalised = raw / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

/**
 * Bounds that keep every value clear of the plot edges.
 *
 * Snapping alone is not enough. Saturday's low was $2,407 and the step 200, so
 * the floor snapped to $2,400 and left the marker seven dollars off the axis --
 * under 1% of the plot height, which is drawn as "on the line". The clearance
 * is therefore a proportion of the axis range, not a test for landing exactly
 * on it: a bound too close to its data drops a further step.
 *
 * Zero is exempt. A floor of zero needs no clearance beneath it, because
 * nothing is being obscured -- the reader is not going to mistake the axis for
 * a value there.
 *
 * Values are read from every series on the chart, benchmark lines included --
 * an axis fitted to one series clips the other.
 */
const MIN_CLEARANCE = 0.04;

export function niceYBounds(...series: Array<Array<number | null>>): {
  min: number;
  max: number;
} {
  const values = series.flat().filter((v): v is number => v !== null && Number.isFinite(v));
  if (values.length === 0) return { min: 0, max: 10 };

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const step = niceStep(dataMax - dataMin || Math.abs(dataMax) || 1);

  let min = Math.floor(dataMin / step) * step;
  let max = Math.ceil(dataMax / step) * step;
  // <= not <: a flat series has a zero range, so a strict comparison can
  // never fire and the axis collapses to a single value with no height.
  if (dataMin - min <= (max - min) * MIN_CLEARANCE) min -= step;
  if (max - dataMax <= (max - min) * MIN_CLEARANCE) max += step;

  // A count of hours, doctors or days has no negative reading, so an axis that
  // shows one is always wrong. Applied after the clearance step, which may have
  // reached below zero to make room that zero does not need.
  //
  // Data that genuinely goes negative -- available time does, when a slot is
  // double booked -- keeps its negative axis.
  if (dataMin >= 0) min = Math.max(0, min);

  return { min, max };
}
